import { useCallback, useMemo, useRef, useState } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { fetchBlogFeedArticles } from '@/lib/blog/blog-client';
import { isRendezVousUpcoming, toEndedAtMs } from '@jeraisonne/db/visio';
import { isBlogArticleVisibleToRole } from '@jeraisonne/db';
import { useDiscussions } from '@/lib/discussions/queries';
import { useMyPrivateLives } from '@/lib/private-live';
import { useStudentSkillcards } from '@/lib/cards/student-skillcards';
import { refetchFeed } from './feed-refresh';
import type {
  AssignmentRow,
  AttemptRow,
  AuthorsById,
  ChallengeRow,
  DiagnosticFeedRow,
  EvaluationFeedRow,
  FeedItem,
  QuestionReportFeedRow,
  DailyRecapFeedRow,
  SubmissionFeedRow,
  VisioFeedRow,
  ViewerRole,
} from '@/components/news/types';
import {
  FEED_PAGE_SIZE,
  keysetFilter,
  mergeFeedSources,
  nextCursor,
  sourcesToAdvance,
  type FeedCursor,
  type FeedPage,
  type FeedSourceState,
} from './feed-paging';

export type AssignmentScope =
  | { type: 'student'; studentId: string }
  | { type: 'tutor'; tutorId: string }
  | { type: 'parent'; studentIds: string[] }
  /** Admin, Feed « tout le monde » : tous les devoirs (RLS admin_select, 0347). */
  | { type: 'all' };

/** Envois d'exercices photo (RLS : l'élève voit les siens, le prof ceux reçus). */
export type SubmissionScope =
  | { type: 'tutor'; tutorId: string }
  | { type: 'student'; studentId: string }
  | { type: 'all' };

export type UseNewsFeedOptions = {
  /** Scope ciblant la table assignments */
  assignmentScope: AssignmentScope;
  /** user_id à inclure pour challenge_posts */
  challengeUserIds: string[];
  /** student_id à inclure pour attempts (essais terminés) */
  attemptStudentIds: string[];
  /** clé pour distinguer le cache React Query par contexte/utilisateur */
  scopeKey: string;
  /** Rôle du viewer — filtre les articles de blog par `target_roles`. */
  viewerRole: ViewerRole;
  /** Envois d'exercices photo à inclure (prof destinataire / élève expéditeur). */
  submissionScope?: SubmissionScope;
  /** Inclure les visios / réunions TERMINÉES auxquelles l'utilisateur a pris part. */
  includeVisio?: boolean;
  /** Élève dont on montre les SKILLCARDS ajoutées par un tuteur (migration 0277). */
  skillcardStudentId?: string;
  /** Élèves dont on montre les notes d'éval saisies (soi-même / élèves du prof, 0321-0324). */
  evaluationStudentIds?: string[];
  /** Enfants dont on montre les diagnostics terminés (Feed parent, 0325). */
  diagnosticStudentIds?: string[];
  /**
   * Admin, Feed « tout le monde » (migration 0347) : essais, défis, notes et
   * diagnostics de TOUS les comptes (les listes d'ids sont alors ignorées).
   */
  allUsers?: boolean;
  /**
   * Visios limitées à celles où ce compte est tuteur ou élève. Pour l'admin
   * hors mode « tout le monde » : la RLS (`video_rooms: admin_read`) lui
   * renverrait sinon toutes les salles.
   */
  visioOwnerId?: string;
  /**
   * Questions signalées (migration 0348). La RLS ne renvoie que ce que le
   * viewer peut voir : tous les signalements pour un admin, ceux de SES
   * exercices pour un auteur.
   */
  includeQuestionReports?: boolean;
  /** Admin : récap journalier de l'activité (migration 0523, 60 derniers jours). */
  includeDailyRecaps?: boolean;
  /**
   * Admin : essais COMMENCÉS mais pas terminés — en cours OU abandonnés —,
   * des élèves suivis ou de tout le monde en mode `allUsers` (migration 0504).
   */
  includeStartedAttempts?: boolean;
  enabled?: boolean;
};

export type UseNewsFeedResult = {
  /** Éléments affichables (sources fusionnées, du plus récent au plus ancien). */
  items: FeedItem[];
  authorsById: AuthorsById;
  /** Clés `${student_id}:${exercise_id}` pour les assignments déjà terminés (attempt completed). */
  doneAssignmentKeys: Set<string>;
  isLoading: boolean;
  /** Il reste des éléments plus anciens à charger. */
  hasMore: boolean;
  /** Une page plus ancienne est en cours de chargement. */
  isFetchingMore: boolean;
  /** Charge la suite du fil (sources en retard seulement). */
  loadMore: () => void;
  /** Recharge tout le fil depuis le serveur (bouton, tirer-pour-rafraîchir). */
  refresh: () => Promise<void>;
  /** Un rechargement demandé par l'utilisateur est en cours. */
  isRefreshing: boolean;
};

function uniq(ids: (string | null | undefined)[]): string[] {
  return Array.from(new Set(ids.filter((id): id is string => !!id)));
}

// Table postérieure aux types générés → client générique.
const db = () => supabase as unknown as SupabaseClient;

/**
 * Une source du Feed lue page par page (curseur date + id, plus récent d'abord).
 * `toItem` doit être STABLE (fonction de module) : il entre dans un useMemo.
 */
function usePagedFeedSource<Row>(opts: {
  queryKey: readonly unknown[];
  enabled: boolean;
  fetchPage: (cursor: FeedCursor | null) => Promise<FeedPage<Row>>;
  toItem: (row: Row) => FeedItem;
}) {
  const { queryKey, enabled, fetchPage, toItem } = opts;
  const query = useInfiniteQuery({
    queryKey,
    enabled,
    staleTime: 60_000,
    initialPageParam: null as FeedCursor | null,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const rows = useMemo(() => query.data?.pages.flatMap((p) => p.rows) ?? [], [query.data]);
  const lastNext = query.data?.pages.at(-1)?.next ?? null;
  const state = useMemo<FeedSourceState>(
    // Une source en erreur ne bloque jamais le fil : elle n'a plus de « suite ».
    () => ({ items: rows.map(toItem), next: query.isError ? null : lastNext }),
    [rows, toItem, lastNext, query.isError],
  );
  return {
    rows,
    state,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  };
}

/** Page ordonnée `column desc, id desc` après le curseur. */
function pageOf<
  Q extends {
    order: (c: string, o: { ascending: boolean }) => Q;
    or: (f: string) => Q;
    limit: (n: number) => Q;
  },
>(query: Q, column: string, cursor: FeedCursor | null): Q {
  const q = cursor ? query.or(keysetFilter(column, cursor)) : query;
  return q
    .order(column, { ascending: false })
    .order('id', { ascending: false })
    .limit(FEED_PAGE_SIZE);
}

function page<Row>(
  rows: Row[],
  cursorOf: (row: Row) => FeedCursor,
  rawRows: unknown[] = rows,
): FeedPage<Row> {
  return { rows, next: nextCursor(rawRows as Row[], FEED_PAGE_SIZE, cursorOf) };
}

const assignmentCursor = (r: AssignmentRow): FeedCursor => ({ date: r.assigned_at, id: r.id });
const challengeCursor = (r: ChallengeRow): FeedCursor => ({ date: r.created_at, id: r.id });
const attemptCursor = (r: AttemptRow): FeedCursor => ({ date: r.completed_at ?? '', id: r.id });
const startedAttemptCursor = (r: AttemptRow): FeedCursor => ({
  date: r.started_at ?? '',
  id: r.id,
});
const submissionCursor = (r: SubmissionFeedRow): FeedCursor => ({ date: r.created_at, id: r.id });
const visioCursor = (r: VisioFeedRow): FeedCursor => ({ date: r.starts_at, id: r.id });
const evaluationCursor = (r: EvaluationFeedRow): FeedCursor => ({
  date: r.graded_at ?? r.eval_date,
  id: r.id,
});
const questionReportCursor = (r: QuestionReportFeedRow): FeedCursor => ({
  date: r.created_at,
  id: r.id,
});
const questionReportItem = (d: QuestionReportFeedRow): FeedItem => ({
  kind: 'question_report',
  date: d.created_at,
  data: d,
});
const diagnosticCursor = (r: DiagnosticFeedRow): FeedCursor => ({
  date: r.completed_at ?? '',
  id: r.id,
});

const assignmentItem = (d: AssignmentRow): FeedItem => ({
  kind: 'assignment',
  date: d.assigned_at,
  data: d,
});
const challengeItem = (d: ChallengeRow): FeedItem => ({
  kind: 'challenge',
  date: d.created_at,
  data: d,
});
const attemptItem = (d: AttemptRow): FeedItem => ({
  kind: 'attempt',
  date: d.completed_at ?? '',
  data: d,
});
const startedAttemptItem = (d: AttemptRow): FeedItem => ({
  kind: 'attempt_started',
  date: d.started_at ?? '',
  data: d,
});
const submissionItem = (d: SubmissionFeedRow): FeedItem => ({
  kind: 'submission',
  date: d.created_at,
  data: d,
});
const visioItem = (d: VisioFeedRow): FeedItem => ({
  kind: 'visio',
  date: d.ended_at ?? d.ends_at,
  data: d,
});
const evaluationItem = (d: EvaluationFeedRow): FeedItem => ({
  kind: 'evaluation',
  date: d.graded_at ?? d.eval_date,
  data: d,
});
const diagnosticItem = (d: DiagnosticFeedRow): FeedItem => ({
  kind: 'diagnostic',
  date: d.completed_at ?? '',
  data: d,
});

export function useNewsFeed(opts: UseNewsFeedOptions): UseNewsFeedResult {
  const {
    assignmentScope,
    challengeUserIds,
    attemptStudentIds,
    scopeKey,
    viewerRole,
    submissionScope,
    includeVisio = false,
    skillcardStudentId,
    evaluationStudentIds = [],
    diagnosticStudentIds = [],
    allUsers = false,
    visioOwnerId,
    includeQuestionReports = false,
    includeDailyRecaps = false,
    includeStartedAttempts = false,
    enabled = true,
  } = opts;

  const skillcardsQuery = useStudentSkillcards(enabled ? skillcardStudentId : undefined);

  const assignments = usePagedFeedSource<AssignmentRow>({
    queryKey: ['news-assignments', scopeKey],
    enabled,
    toItem: assignmentItem,
    fetchPage: async (cursor) => {
      let query = supabase
        .from('assignments')
        .select(
          `id, assigned_at, due_at, status, class_id, exercise_id, student_id, tutor_id,
           exercises(id, title, illustration_url, chapters(title, subject, level, slug, illustration_url)),
           profiles!assignments_tutor_id_fkey(display_name:visible_name, avatar_url)`,
        )
        .in('status', ['pending', 'in_progress']);

      if (assignmentScope.type === 'student') {
        query = query.eq('student_id', assignmentScope.studentId);
      } else if (assignmentScope.type === 'tutor') {
        query = query.eq('tutor_id', assignmentScope.tutorId);
      } else if (assignmentScope.type === 'all') {
        // Admin : tous les devoirs, aucun filtre.
      } else {
        if (assignmentScope.studentIds.length === 0) return { rows: [], next: null };
        query = query.in('student_id', assignmentScope.studentIds);
      }

      const { data, error } = await pageOf(query, 'assigned_at', cursor);
      if (error) throw error;
      return page((data ?? []) as unknown as AssignmentRow[], assignmentCursor);
    },
  });

  const challenges = usePagedFeedSource<ChallengeRow>({
    queryKey: ['news-challenges', scopeKey],
    enabled: enabled && (allUsers || challengeUserIds.length > 0),
    toItem: challengeItem,
    fetchPage: async (cursor) => {
      const base = supabase
        .from('challenge_posts')
        .select(
          'id, user_id, title, challenge_slug, total_attempts, total_success, total_views, created_at, thumbnail_url, exercises(title)',
        );
      const query = allUsers ? base : base.in('user_id', challengeUserIds);
      const { data, error } = await pageOf(query, 'created_at', cursor);
      if (error) throw error;
      return page((data ?? []) as unknown as ChallengeRow[], challengeCursor);
    },
  });

  const attempts = usePagedFeedSource<AttemptRow>({
    queryKey: ['news-attempts', scopeKey],
    enabled: enabled && (allUsers || attemptStudentIds.length > 0),
    toItem: attemptItem,
    fetchPage: async (cursor) => {
      const base = supabase
        .from('attempts')
        .select(
          `id, exercise_id, student_id, completed_at, score,
           exercises(title, illustration_url, chapters(title, subject, level, slug, illustration_url)),
           attempt_steps(was_correct, time_ms)`,
        )
        .eq('status', 'completed');
      const query = allUsers ? base : base.in('student_id', attemptStudentIds);
      const { data, error } = await pageOf(query, 'completed_at', cursor);
      if (error) throw error;
      return page((data ?? []) as unknown as AttemptRow[], attemptCursor);
    },
  });

  // Admin : essais commencés, pas terminés — en cours ou abandonnés (migration
  // 0504). Triés sur leur début (curseur commun aux deux statuts). Le nombre
  // de questions vient d'une 2e requête (colonne calculée, top-level seulement).
  const startedAttempts = usePagedFeedSource<AttemptRow>({
    queryKey: ['news-started-attempts', scopeKey],
    enabled: enabled && includeStartedAttempts && (allUsers || attemptStudentIds.length > 0),
    toItem: startedAttemptItem,
    fetchPage: async (cursor) => {
      const base = db()
        .from('attempts')
        .select(
          `id, exercise_id, student_id, started_at, completed_at, abandoned_at, score, status,
           exercises(title, illustration_url, chapters(title, subject, level, slug, illustration_url)),
           attempt_steps(was_correct, time_ms)`,
        )
        .in('status', ['in_progress', 'abandoned'])
        .not('exercise_id', 'is', null);
      const query = allUsers ? base : base.in('student_id', attemptStudentIds);
      const { data, error } = await pageOf(query, 'started_at', cursor);
      if (error) throw error;
      const rows = (data ?? []) as unknown as AttemptRow[];
      const exerciseIds = uniq(rows.map((r) => r.exercise_id));
      if (exerciseIds.length === 0) return page(rows, startedAttemptCursor);
      const counts = await db()
        .from('exercises')
        .select('id, question_count:exercise_question_count')
        .in('id', exerciseIds);
      // Nombre de questions absent = barre limitée aux réponses données.
      const byId = new Map(
        ((counts.data ?? []) as { id: string; question_count: number | null }[]).map((e) => [
          e.id,
          e.question_count,
        ]),
      );
      return page(
        rows.map((r) => ({ ...r, question_count: byId.get(r.exercise_id) ?? null })),
        startedAttemptCursor,
      );
    },
  });

  // Envois d'exercices photo (migrations 0222-0223). Deux FK vers `profiles`
  // → embeds désambiguïsés (vérifié : pas de PGRST201).
  const submissionOwnerId = !submissionScope
    ? ''
    : submissionScope.type === 'all'
      ? 'all'
      : submissionScope.type === 'tutor'
        ? submissionScope.tutorId
        : submissionScope.studentId;
  const submissions = usePagedFeedSource<SubmissionFeedRow>({
    queryKey: ['news-submissions', submissionScope?.type ?? 'none', submissionOwnerId],
    enabled: enabled && !!submissionScope && !!submissionOwnerId,
    toItem: submissionItem,
    fetchPage: async (cursor) => {
      const scope = submissionScope!;
      const base = db()
        .from('exercise_submissions')
        .select(
          `id, student_id, tutor_id, note, status, created_at,
           student:profiles!exercise_submissions_student_id_fkey(display_name:visible_name, avatar_url),
           tutor:profiles!exercise_submissions_tutor_id_fkey(display_name:visible_name, avatar_url),
           chapters(title),
           exercise_submission_photos(storage_path, position)`,
        );
      const query =
        scope.type === 'all'
          ? base
          : base.eq(scope.type === 'tutor' ? 'tutor_id' : 'student_id', submissionOwnerId);
      const { data, error } = await pageOf(query, 'created_at', cursor);
      if (error) throw error;
      return page((data ?? []) as unknown as SubmissionFeedRow[], submissionCursor);
    },
  });

  // Visios / réunions passées (migrations 0233-0259). La RLS `video_rooms`
  // (`is_visio_room_member`) ne renvoie que les salles dont l'utilisateur est
  // membre : rendez-vous (tuteur, élève) ET réunions libres. Une salle n'existe
  // qu'une fois rejointe → seules les visios qui ont réellement eu lieu.
  // 2 FK vers `profiles` → embeds désambiguïsés (vérifié : pas de PGRST201).
  // Curseur sur `starts_at` ; les salles pas encore terminées sont écartées APRÈS
  // la lecture (la page suivante part quand même de la dernière ligne lue).
  const visio = usePagedFeedSource<VisioFeedRow>({
    queryKey: ['news-visio', scopeKey, visioOwnerId ?? ''],
    enabled: enabled && includeVisio,
    toItem: visioItem,
    fetchPage: async (cursor) => {
      const query = db()
        .from('video_rooms')
        .select(
          `id, reservation_id, kind, title, tutor_id, student_id, starts_at, ends_at, ended_at,
           tutor:profiles!video_rooms_tutor_id_fkey(display_name:visible_name, avatar_url),
           student:profiles!video_rooms_student_id_fkey(display_name:visible_name, avatar_url)`,
        )
        .lte('starts_at', new Date().toISOString());
      const scoped =
        visioOwnerId && !allUsers
          ? query.or(`tutor_id.eq.${visioOwnerId},student_id.eq.${visioOwnerId}`)
          : query;
      const { data, error } = await pageOf(scoped, 'starts_at', cursor);
      if (error) throw error;
      const raw = (data ?? []) as unknown as VisioFeedRow[];
      const now = Date.now();
      const ended = raw.filter(
        (r) => !isRendezVousUpcoming(now, new Date(r.ends_at).getTime(), toEndedAtMs(r.ended_at)),
      );
      return page(ended, visioCursor, raw);
    },
  });

  // Notes d'éval saisies par l'élève (0321) : RLS élève lui-même + prof lié
  // (0322). Une seule FK vers `profiles` → embed nommé par prudence.
  const evaluations = usePagedFeedSource<EvaluationFeedRow>({
    queryKey: ['news-evaluations', scopeKey, [...evaluationStudentIds].sort().join(',')],
    enabled: enabled && (allUsers || evaluationStudentIds.length > 0),
    toItem: evaluationItem,
    fetchPage: async (cursor) => {
      const base = db()
        .from('student_evaluations')
        .select(
          `id, student_id, title, subject, eval_date, score, max_score, coefficient, graded_at,
           student:profiles!student_evaluations_student_id_fkey(display_name:visible_name, avatar_url)`,
        )
        .not('score', 'is', null);
      const query = allUsers ? base : base.in('student_id', evaluationStudentIds);
      const { data, error } = await pageOf(query, 'graded_at', cursor);
      if (error) throw error;
      const rows = ((data ?? []) as Record<string, unknown>[]).map(
        (r) =>
          ({
            ...r,
            score: Number(r.score),
            max_score: Number(r.max_score),
            coefficient: Number(r.coefficient),
          }) as unknown as EvaluationFeedRow,
      );
      return page(rows, evaluationCursor);
    },
  });

  // Diagnostics terminés des enfants (RLS parent_select, migration 0325).
  const diagnostics = usePagedFeedSource<DiagnosticFeedRow>({
    queryKey: ['news-diagnostics', scopeKey, [...diagnosticStudentIds].sort().join(',')],
    enabled: enabled && (allUsers || diagnosticStudentIds.length > 0),
    toItem: diagnosticItem,
    fetchPage: async (cursor) => {
      const base = db()
        .from('skill_diagnostics')
        .select(
          `id, student_id, completed_at, mastery_pct, correct, total, report,
           student:profiles!skill_diagnostics_student_id_fkey(display_name:visible_name, avatar_url)`,
        )
        .eq('status', 'completed');
      const query = allUsers ? base : base.in('student_id', diagnosticStudentIds);
      const { data, error } = await pageOf(query, 'completed_at', cursor);
      if (error) throw error;
      return page((data ?? []) as unknown as DiagnosticFeedRow[], diagnosticCursor);
    },
  });

  // Questions signalées (migration 0348). 3 FK vers `profiles` → embed nommé.
  const questionReports = usePagedFeedSource<QuestionReportFeedRow>({
    queryKey: ['news-question-reports', scopeKey],
    enabled: enabled && includeQuestionReports,
    toItem: questionReportItem,
    fetchPage: async (cursor) => {
      const query = db()
        .from('question_reports')
        .select(
          `id, exercise_id, question_block_id, reporter_id, kind, message, question_excerpt, status, created_at,
           reporter:profiles!question_reports_reporter_id_fkey(display_name:visible_name, avatar_url),
           exercises(title)`,
        );
      const { data, error } = await pageOf(query, 'created_at', cursor);
      if (error) throw error;
      return page((data ?? []) as unknown as QuestionReportFeedRow[], questionReportCursor);
    },
  });

  // Articles de blog publiés — identiques pour tous les viewers d'un même rôle
  // (clé indépendante du `scopeKey`, meilleur partage de cache). Le filtre
  // `target_roles` se fait CLIENT (pas de RLS dédiée, cf. migration 0220).
  // Source courte, chargée d'un coup : elle ne retient jamais le fil.
  const blogQuery = useQuery({
    queryKey: ['news-blog', viewerRole],
    enabled,
    staleTime: 60_000,
    queryFn: () => fetchBlogFeedArticles(),
  });

  // Discussions (migrations 0265-0267) : hook DÉJÀ scopé à l'utilisateur
  // courant (tutor_id/student_id = auth.uid()) — un parent/joueur obtient
  // naturellement [] (aucune discussion ne les concerne jamais), même
  // traitement que blogQuery (pas de filtre par rôle côté requête).
  const discussionsQuery = useDiscussions();

  // Défis live privés reçus ou organisés (migration 0650), 30 derniers jours.
  const privateLivesQuery = useMyPrivateLives(enabled);

  // Récap journalier admin (migration 0523) : une carte par jour, chargées d'un
  // coup (60 derniers jours, RLS admin).
  const dailyRecapsQuery = useQuery({
    queryKey: ['news-daily-recaps'],
    enabled: enabled && includeDailyRecaps,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<DailyRecapFeedRow[]> => {
      const { data, error } = await db()
        .from('admin_daily_recaps')
        .select('day, stats, created_at')
        .order('day', { ascending: false })
        .limit(60);
      if (error) throw error;
      return (
        (data ?? []) as { day: string; stats: DailyRecapFeedRow['stats']; created_at: string }[]
      ).map((r) => ({ id: r.day, day: r.day, stats: r.stats, created_at: r.created_at }));
    },
  });

  // Sources chargées d'un coup (aucune « suite ») : blog, discussions, SKILLCARDS.
  const staticSource = useMemo<FeedSourceState>(() => {
    const items: FeedItem[] = [
      ...(blogQuery.data ?? [])
        .filter((a) => isBlogArticleVisibleToRole(a.target_roles, viewerRole))
        .map((d) => ({ kind: 'blog' as const, date: d.published_at ?? d.created_at, data: d })),
      ...(discussionsQuery.data ?? []).map((d) => ({
        kind: 'discussion' as const,
        date: d.lastMessageAt,
        data: d,
      })),
      ...(privateLivesQuery.data ?? []).map((d) => ({
        kind: 'private_live' as const,
        date: d.invited_at,
        data: d,
      })),
      ...(includeDailyRecaps ? (dailyRecapsQuery.data ?? []) : []).map((d) => ({
        kind: 'daily_recap' as const,
        date: d.created_at,
        data: d,
      })),
      ...(skillcardsQuery.data ?? [])
        .filter((c) => c.assignment)
        .map((c) => ({
          kind: 'card_assignment' as const,
          date: c.assignment!.assigned_at,
          data: c,
        })),
    ];
    return { items, next: null };
  }, [
    blogQuery.data,
    viewerRole,
    discussionsQuery.data,
    skillcardsQuery.data,
    dailyRecapsQuery.data,
    includeDailyRecaps,
    privateLivesQuery.data,
  ]);

  const paged = [
    assignments,
    challenges,
    attempts,
    startedAttempts,
    submissions,
    visio,
    evaluations,
    diagnostics,
    questionReports,
  ];
  const sourceStates = useMemo(
    () => [
      assignments.state,
      challenges.state,
      attempts.state,
      startedAttempts.state,
      submissions.state,
      visio.state,
      evaluations.state,
      diagnostics.state,
      questionReports.state,
      staticSource,
    ],
    [
      assignments.state,
      challenges.state,
      attempts.state,
      startedAttempts.state,
      submissions.state,
      visio.state,
      evaluations.state,
      diagnostics.state,
      questionReports.state,
      staticSource,
    ],
  );

  const items = useMemo(() => mergeFeedSources(sourceStates), [sourceStates]);
  const advance = useMemo(() => sourcesToAdvance(sourceStates), [sourceStates]);
  const isFetchingMore = paged.some((p) => p.isFetchingNextPage);

  // Charge la page suivante des seules sources qui retiennent le fil (index
  // alignés sur `sourceStates` ; la source statique n'est jamais retenue).
  const pagedRef = useRef(paged);
  pagedRef.current = paged;
  const loadMore = useCallback(() => {
    for (const i of advance) {
      const source = pagedRef.current[i];
      if (source && !source.isFetchingNextPage) void source.fetchNextPage();
    }
  }, [advance]);

  // ── Détecte les assignments dont l'exercice a déjà été terminé par l'élève
  // (finish_attempt ne met pas assignment.status à 'completed', donc on regarde
  // directement la table attempts).
  const assignmentExerciseIds = useMemo(
    () => uniq(assignments.rows.map((a) => a.exercise_id)),
    [assignments.rows],
  );
  const assignmentStudentIds = useMemo(
    () => uniq(assignments.rows.map((a) => a.student_id)),
    [assignments.rows],
  );

  const doneAttemptsQuery = useQuery({
    queryKey: [
      'news-assignments-done',
      scopeKey,
      [...assignmentExerciseIds].sort().join(','),
      [...assignmentStudentIds].sort().join(','),
    ],
    enabled: enabled && assignmentExerciseIds.length > 0 && assignmentStudentIds.length > 0,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('attempts')
        .select('student_id, exercise_id')
        .eq('status', 'completed')
        .in('student_id', assignmentStudentIds)
        .in('exercise_id', assignmentExerciseIds);
      if (error) throw error;
      return (data ?? []) as { student_id: string; exercise_id: string }[];
    },
  });

  const doneAssignmentKeys = useMemo(() => {
    const set = new Set<string>();
    for (const a of doneAttemptsQuery.data ?? []) {
      set.add(`${a.student_id}:${a.exercise_id}`);
    }
    return set;
  }, [doneAttemptsQuery.data]);

  const authorIds = useMemo(() => {
    const ids: string[] = [];
    for (const a of attempts.rows) ids.push(a.student_id);
    for (const a of startedAttempts.rows) ids.push(a.student_id);
    for (const c of challenges.rows) ids.push(c.user_id);
    for (const a of assignments.rows) ids.push(a.student_id);
    return uniq(ids);
  }, [assignments.rows, challenges.rows, attempts.rows, startedAttempts.rows]);

  // Les auteurs se complètent à chaque nouvelle page : on garde les anciens
  // affichés pendant la lecture (pas de « Élève » qui clignote).
  const authorsQuery = useQuery({
    queryKey: ['news-authors', scopeKey, [...authorIds].sort().join(',')],
    enabled: enabled && authorIds.length > 0,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name:visible_name, avatar_url, role')
        .in('id', authorIds);
      if (error) throw error;
      return data ?? [];
    },
  });

  const authorsById = useMemo<AuthorsById>(() => {
    const map: AuthorsById = new Map();
    for (const p of authorsQuery.data ?? []) {
      map.set(p.id, {
        display_name: p.display_name,
        avatar_url: p.avatar_url,
        role: p.role,
      });
    }
    return map;
  }, [authorsQuery.data]);

  // ── Rechargement explicite (bouton, tirer-pour-rafraîchir) ────────────────
  // `refetchQueries` sur une source paginée recharge TOUTES les pages déjà
  // lues : les nouveautés remontent en tête sans perdre la profondeur de
  // lecture (on ne renvoie pas l'utilisateur en haut du fil).
  const qc = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetchFeed(qc);
    } finally {
      setIsRefreshing(false);
    }
  }, [qc]);

  return {
    items,
    authorsById,
    doneAssignmentKeys,
    isLoading:
      paged.some((p) => p.isLoading) ||
      blogQuery.isLoading ||
      authorsQuery.isLoading ||
      discussionsQuery.isLoading,
    hasMore: advance.length > 0,
    isFetchingMore,
    loadMore,
    refresh,
    isRefreshing,
  };
}
